/**
 * What the value legend says: one short name and one number per plot, for the
 * bar under the pointer.
 *
 * A chart states a plot's reading only at the **right edge** — that is what the
 * `AxisMarkerAnnotation`s are — so at any other bar the number has to be traced
 * off the line by eye. This is the rule for saying it out loud.
 *
 * Here rather than in the builder for the reason everything decidable in this
 * package is: the builder needs WebGL and a WASM engine, and none of naming a
 * row, rounding a number or deciding to omit one needs a chart to state.
 *
 * **No date.** The crosshair already labels the X axis with the bar's date, and
 * a legend repeating it would print the same value twice in two places that can
 * disagree after a resample.
 */

import { COMPACT_FROM, compactNumber } from "./compact.js";
import type { SeriesPlan } from "./pane-plan.js";
import { signedLogLabel, toSignedLog } from "./signed-log.js";

/**
 * One number in a row, with the letter that says which it is.
 *
 * Labelled because a candle has four and they are only telling apart by name.
 * An ordinary plot has one and no label — the row's own name already says what
 * the number is, and "sma 50 V: 43.7" would be labelling it twice.
 */
export interface LegendCell {
  label?: string;
  /**
   * A word printed before the value **without** the colon a `label` takes —
   * `cc -0.85%`. A label names one of the bar's own fields; a tag names a
   * reading derived from them, and the two are told apart on sight.
   */
  tag?: string;
  /** The formatted value, or **empty** where this bar has none. */
  value: string;
  /**
   * Which way a signed reading points, for the host to colour; absent for a
   * level (a price, an average) and for a change of exactly zero.
   */
  tone?: "up" | "down";
}

/** One rendered row: what to print, and the colour of the plot it describes. */
export interface LegendRow {
  plotId: string;
  /** The short name — an indicator alias, or the symbol for a price series. */
  name: string;
  cells: LegendCell[];
  /** The plot's own stroke, so the row can be read back to the line it names. */
  color?: string;
  /** Drawn dimmed, and the only thing that can turn it back on. */
  hidden: boolean;
  /**
   * A projected continuation rather than a measurement.
   *
   * The host gathers these under one heading instead of letting each sit beside
   * the line it continues: with six indicators on a chart the legend otherwise
   * doubles in length and every reading appears twice, once as a fact and once
   * as a forecast, three pixels apart.
   *
   * Decided here because this is the layer that holds the alias. The row itself
   * carries no alias — deliberately, since its `name` is what a person reads —
   * so a host wanting to know would have to re-derive it from the plot id.
   */
  forward?: boolean;
}

/** The four fields a candle is drawn from, in the order a reader expects. */
const OHLC_FIELDS = ["open", "high", "low", "close"] as const;

/**
 * Which close a pane's averages are measured against, or none.
 *
 * The price plot's own column, so the distance beside an average and the `C:`
 * the row above prints are the same number — on a Heikin-Ashi chart those are
 * two different columns of one frame.
 *
 * `closeKey` is **undefined for another symbol's candles** (`ohlc@SPY`): the
 * averages on that pane are this chart's, so a distance from SPY's close would
 * be two instruments in one percentage. The same reason `isAverage` refuses an
 * `@SYM` alias.
 */
interface PriceRead {
  closeKey?: string;
}

/** The price plot of a pane, and the close its averages should read. */
function priceRead(series: readonly SeriesPlan[]): PriceRead {
  const price = series.find((sp) => sp.dataKind === "ohlc" && !sp.signal);
  if (!price) return { closeKey: "close" };
  if (price.outputAlias?.includes("@")) return {};
  return { closeKey: legendColumn(price, "close") ?? "close" };
}

/** The letter each gets in the legend — the notation every chart tool uses. */
const OHLC_LABELS: Record<(typeof OHLC_FIELDS)[number], string> = {
  open: "O",
  high: "H",
  low: "L",
  close: "C",
};

/**
 * The name for a plot's row.
 *
 * `outputAlias` is the short name already: it is what the DSL calls the series
 * (`sma20`, `rsi14`, `macdSignal`), what the request asked the server for, and
 * what the indicator picker prints. Deriving a prettier label from the
 * indicator catalog was the alternative and is worse in the case that matters —
 * two plots of the same family (`macd` and `macdSignal`) would both come back
 * "MACD" and the legend would name neither.
 *
 * A base price series carries the column name for the same reason: `close` is
 * what it is, and the pointer's own y is *not* that number — the crosshair's
 * axis label follows the cursor, this follows the bar.
 *
 * The underscores go. An alias joins its parameters with them — `sma_50`,
 * `macd_12_26_9`, `ceLong_22_3_1` — which is right for a column name and reads
 * as code in a legend. Spaces are the only change: the *words* stay exactly as
 * stored, so what the legend calls a plot is still what the picker calls it and
 * still what an expression would name.
 *
 * The kind is the fallback rather than an empty string, because a row with no
 * name reads as a rendering fault rather than as an unnamed series.
 */
export function legendName(sp: SeriesPlan, symbol?: string, given?: string): string {
  // **A price series is named for the instrument, not for its alias.** `ohlc` is
  // a request spelling — it names no column and describes nothing a reader
  // wants — where `AAPL` is what the bars under the pointer actually are. The
  // alias stays the fallback so a candle still has a name on a chart whose
  // symbol has not arrived yet, which is every chart for its first frame.
  //
  // **Ahead of `given` on purpose.** The catalog knows this plot as "OHLC",
  // which is the one name on the chart that says less than the alias did — the
  // instrument is the whole point of the row.
  if (sp.dataKind === "ohlc" && symbol) return symbol;
  // **A caller who knows the catalog beats the alias.** `ceLong_22_3_1` is a
  // column name; "Long Stop" is what the indicator picker calls that plot, and a
  // legend and a picker disagreeing about what a line is called is a worse
  // problem than either name being imperfect. This layer cannot look it up —
  // the catalog is fetched, and `plan/` does not fetch — so the host resolves it
  // and the rule below is what happens when it cannot.
  if (given) return given;
  return (sp.outputAlias ?? sp.indicatorRef ?? sp.seriesKind).replaceAll("_", " ");
}

/**
 * The column an alias reads its value from.
 *
 * Almost always itself. The exception is OHLC, where one plot is fed **four**
 * columns and the alias names none of them: `ohlc` expands to `open`, `high`,
 * `low` and `close` by substitution, which is the same rule `chart-feed`'s
 * `ohlcColumns` uses — and it has to be substitution rather than a constant,
 * because the alias can carry a symbol (`ohlc@SPY` → `close@SPY`).
 *
 * `field` picks which of the four; the legend asks for all of them in turn, and
 * `close` is the default because it is what a bar's value *is* when a bar has to
 * have exactly one — SciChart's own `SeriesInfo.yValue` for an OHLC series is the
 * close, so anything reading one number agrees with a hit test by construction.
 */
export function legendColumn(sp: SeriesPlan, field = "close"): string | undefined {
  const alias = sp.outputAlias;
  if (!alias) return undefined;
  if (sp.dataKind !== "ohlc") return alias;
  // **A derived bar set** — `haOhlc` → `haOpen`…`haClose`, `chart-feed`'s
  // `derivedCandleColumns` rule. The capital is why substitution missed it, and
  // a Heikin-Ashi chart's whole price row read blank.
  if (alias.endsWith("Ohlc") && alias.length > "Ohlc".length) {
    return alias.slice(0, -"Ohlc".length) + field[0]!.toUpperCase() + field.slice(1);
  }
  return alias.replaceAll("ohlc", field);
}

/**
 * Significant-figure-ish rounding, chosen from the magnitude of the number.
 *
 * A fixed precision is wrong on this chart in both directions at once: two
 * decimals turns a volume of 12,345,678 into noise and rounds a MACD histogram
 * of 0.0123 to 0.01, which is a different reading rather than a rougher one.
 * The axis' own label provider was the other candidate and is the wrong source —
 * it is fitted to the *tick spacing*, so a pane zoomed out prints 43 where the
 * bar reads 43.71, and a legend that disagrees with the line it names is worse
 * than one formatted independently.
 *
 * Thresholds rather than `toPrecision`, because `toPrecision` reaches for
 * exponential notation on both ends — `1.2e+7` in a price legend is a number
 * nobody reads at a glance.
 *
 * **Nothing, not a dash, where the bar has no value.** A dash was the first
 * spelling and it is louder than the absence it reports: an SMA-200 has none for
 * its first 199 bars, so sweeping the pointer left through the warm-up filled the
 * legend with punctuation that reads as a fault rather than as "not yet". The
 * name alone says the plot is there and this bar has nothing — and it says it
 * without moving anything, which is what a row carrying buttons needs.
 */
export function legendValue(y: number | null | undefined): string {
  if (y === null || y === undefined || !Number.isFinite(y)) return "";
  const size = Math.abs(y);
  // K / M / B in three significant figures — the axes print the same, so a
  // reading and its axis tag agree (owner, 2026-10-06).
  if (size >= COMPACT_FROM) return compactNumber(y);
  if (size === 0) return "0";
  return y.toFixed(decimalsFor(size));
}

/**
 * How many decimals a number this size is printed with, below the K/M range.
 *
 * Below one, the leading zeros carry no information and the digits after them
 * are the whole reading — a MACD histogram lives there for its entire life.
 */
function decimalsFor(size: number): number {
  if (size >= 100) return 2;
  if (size >= 1) return 3;
  return 5;
}

/**
 * The single-line moving averages, by catalog name, that carry a distance from
 * the close.
 *
 * **A list, not the catalog's category.** "Overlap Studies" also holds
 * Envelope, Pivot and Ichimoku, which are not averages, and Alligator, which is
 * three of them and was left out by the owner's call. Keyed by the alias head,
 * which for all eleven is the catalog name itself (`sma_50` → `sma`).
 */
export const MOVING_AVERAGES: ReadonlySet<string> = new Set([
  "sma",
  "ema",
  "wma",
  "rma",
  "hma",
  "dema",
  "tema",
  "vwma",
  "alma",
  "kama",
  "mcginley",
]);

/** A change as a signed percentage — `-1.64%`, `0.45%`; no `+`, the colour says it. */
export function legendPercent(from: number | undefined, to: number | undefined): LegendCell {
  if (!isReading(from) || !isReading(to) || from === 0) return { value: "" };
  const pct = ((to - from) / from) * 100;
  return signed(pct, `${pct.toFixed(2)}%`);
}

/**
 * A change in price, printed at the decimals the price itself is — so a $5
 * stock's `C: 5.120` sits beside `-0.130`, not `-0.13`.
 */
export function legendChange(from: number | undefined, to: number | undefined): LegendCell {
  if (!isReading(from) || !isReading(to)) return { value: "" };
  const chg = to - from;
  return signed(chg, chg.toFixed(decimalsFor(Math.abs(to))));
}

function isReading(y: number | null | undefined): y is number {
  return y !== null && y !== undefined && Number.isFinite(y);
}

/**
 * The tone of a formatted change — read off the **printed** digits, so a change
 * that rounds to zero is neither colour and never prints as `-0.00`.
 */
function signed(y: number, text: string): LegendCell {
  if (/^-?0(\.0+)?%?$/.test(text)) return { value: text.replace(/^-/, "") };
  return { value: text, tone: y > 0 ? "up" : "down" };
}

/**
 * A line and its own average, by plot id — JAS OBV and its EMA, RS to SPY and
 * its EMA (catalog `gap`, owner 2026-10-06).
 */
export interface LegendGap {
  line: string;
  average: string;
}

/**
 * The gap between a line and its average: `Δ +140K` and `(+12.7%)`, coloured
 * by which side of the average the line is on. Measured over the average's
 * **size**, so a negative OBV average does not flip the sign of the percent.
 * Nothing where either side has no reading, for `legendRows`' reason.
 */
export function gapCells(line: number | null | undefined, average: number | null | undefined): LegendCell[] {
  if (!isReading(line) || !isReading(average)) return [];
  const d = line - average;
  const cells: LegendCell[] = [{ ...signed(d, `${d > 0 ? "+" : ""}${legendValue(d)}`), tag: "Δ" }];
  if (average !== 0) {
    const pct = (d / Math.abs(average)) * 100;
    cells.push(signed(pct, `(${pct > 0 ? "+" : ""}${pct.toFixed(1)}%)`));
  }
  return cells;
}

/** The gap's two plots' readings at one bar, or `undefined` without both. */
export function gapAt(
  series: readonly SeriesPlan[],
  values: Readonly<Record<string, readonly number[]>>,
  index: number,
  gap: LegendGap,
): { line: number; average: number } | undefined {
  const read = (plotId: string) => {
    const sp = series.find((s) => s.plotId === plotId);
    const key = sp && legendColumn(sp);
    return key ? values[key]?.[index] : undefined;
  };
  const line = read(gap.line);
  const average = read(gap.average);
  return isReading(line) && isReading(average) ? { line, average } : undefined;
}

/**
 * The rows for one pane, at one bar.
 *
 * **A hidden plot keeps its row, dimmed.** It was omitted while the legend was
 * read-only, on the reasoning that a row for it is a reading of a line nobody
 * can see. Once the row carries the show/hide control that reasoning inverts:
 * the row is the only thing that can turn the plot back on, and omitting it
 * makes hiding a one-way door out of this surface.
 *
 * **A bar with no value prints nothing, and never a zero.** A warm-up window, a
 * gap in the file, an alias the server declined — all of them are "no reading
 * here", and `0.00` is a number nobody measured. The row itself stays: dropping
 * it would make rows appear and vanish as the pointer sweeps the warm-up, and
 * would take that plot's remove and settings buttons with them.
 *
 * Being past the *last* bar is the caller's to catch, not this function's: there
 * is margin to the right of every chart, so the pointer sits off the end
 * constantly, and a legend that fills with dashes every time reads as the chart
 * breaking. The caller knows the bar count; this only knows the columns, which
 * can legitimately differ in length mid-append.
 *
 * `values` is keyed by alias because that is how the decoded columns arrive, and
 * re-keying them per plot here would duplicate what `chart-feed` already does
 * for the renderer.
 */
export function legendRows(
  series: readonly SeriesPlan[],
  values: Readonly<Record<string, readonly number[]>>,
  index: number,
  symbol?: string,
  nameOf?: (plotId: string) => string | undefined,
  /** Lines with their averages; the average's row also reads the gap. */
  gaps: readonly LegendGap[] = [],
): LegendRow[] {
  if (!Number.isInteger(index) || index < 0) return [];
  const rows: LegendRow[] = [];
  // Once per pane, not once per row: every average in it measures against the
  // same close, and that close is the price plot's own column.
  const price = priceRead(series);
  for (const sp of series) {
    // **A fill has no value of its own.** A band is the area *between* two
    // series and feeds from the same column as the edge drawn over it, so
    // Chandelier's long fill and its long stop are one number printed twice —
    // and the second copy names the fill, which nobody hovers to read. The
    // edges stay; only the shading between them goes.
    if (sp.seriesKind === "band") continue;
    // **Volume-by-Price has no value *at a bar* at all**, which is a stronger
    // version of the same argument. It is a histogram over price: at any given
    // date it is not one number, it is twelve. Its series is never fed, so a row
    // here would be permanently blank — named, coloured with a stroke nothing
    // draws, and carrying nothing. It is skipped by `type` rather than by
    // `seriesKind` because `series.ts` maps it to "column" for want of an honest
    // answer, and every real column does belong in the legend.
    if (sp.plotType === "volumeByPrice") continue;
    rows.push({
      plotId: sp.plotId,
      name: legendName(sp, symbol, nameOf?.(sp.plotId)),
      cells: cellsFor(sp, values, index, price),
      color: sp.stroke,
      hidden: sp.hidden,
      ...(sp.outputAlias?.endsWith("~fwd") ? { forward: true } : {}),
    });
  }
  for (const gap of gaps) {
    const row = rows.find((r) => r.plotId === gap.average);
    const at = gapAt(series, values, index, gap);
    if (row && at) row.cells.push(...gapCells(at.line, at.average));
  }
  return rows;
}

/**
 * A candle gets four numbers and three changes; a moving average gets its value
 * and its distance from the close; everything else gets one.
 *
 * **Four rather than the close alone**, because a bar *is* four numbers and
 * three of them are the ones a reader is hovering to find — where it opened
 * against where it closed is the whole shape of the candle they are pointing at,
 * and the close by itself is the one number the axis marker already shows.
 *
 * They stay on one row rather than becoming four, so the price series reads as
 * one line of the legend like every other plot, and the rows below it do not
 * start four lines further down on a chart that happens to draw candles.
 */
function cellsFor(
  sp: SeriesPlan,
  values: Readonly<Record<string, readonly number[]>>,
  index: number,
  price: PriceRead,
): LegendCell[] {
  // **A signal reads as a direction, not as a number.** Its y is a *lane* — the
  // marker's height in the strip is a position, not a value, which is the whole
  // point of the shape (`plan/signal.ts`). The series behind it is signed
  // though, and *that* is the reading: did an event fire on this bar, and which
  // way. Printing `1` would be the lane's number by coincidence and the event's
  // meaning not at all.
  //
  // The row was skipped entirely at first, which left the one indicator whose
  // whole output is an event with nothing in the legend at all.
  if (sp.signal) {
    const key = legendColumn(sp);
    return [{ value: signalMark(key ? values[key]?.[index] : undefined) }];
  }
  if (sp.dataKind !== "ohlc") {
    const key = legendColumn(sp);
    const y = key ? values[key]?.[index] : undefined;
    // A signed log axis carries growth fractions: 0.33 is +33%, as its labels read.
    const cell = { value: sp.signedLog && typeof y === "number" ? signedLogLabel(toSignedLog(y)) : legendValue(y) };
    // **An average also says how far the close is from it** — negative, and
    // coloured down, when the close is below the line. Over the average, since
    // "the close is 3.7% below the 20 EMA" is the sentence being read.
    //
    // Against **the close this pane prints**, not `values.close`. A
    // Heikin-Ashi chart carries both families (`haClose` beside `close`), so
    // reading the base column there produced a percentage nobody could
    // reproduce from the two numbers next to it — an average sitting exactly on
    // the printed close, tagged 3% away. `price.closeKey` is the price plot's
    // own column, whatever bar source it draws.
    if (!price.closeKey || !isAverage(sp.outputAlias)) return [cell];
    return [cell, legendPercent(y, values[price.closeKey]?.[index])];
  }
  const at = (field: string, bar = index) => {
    const key = legendColumn(sp, field);
    return key ? values[key]?.[bar] : undefined;
  };
  // After the four fields, what the bar *did*: its change from the open as a
  // percentage and in price, then `cc`, its change from the previous close.
  // The previous close is the bar before in the same column — every served
  // interval sends the whole file, so only the first bar ever lacks one.
  return [
    ...OHLC_FIELDS.map((field) => ({
      label: OHLC_LABELS[field],
      value: legendValue(at(field)),
    })),
    legendPercent(at("open"), at("close")),
    legendChange(at("open"), at("close")),
    { ...legendPercent(at("close", index - 1), at("close")), tag: "cc" },
  ];
}

/**
 * Whether an alias is one of {@link MOVING_AVERAGES}, measured against this
 * chart's own close.
 *
 * Not a projection (`~fwd`): past the last bar there is no close to measure
 * from. Not another symbol's (`@SPY`): `close` is this chart's.
 */
function isAverage(alias: string | undefined): boolean {
  if (!alias || alias.endsWith("~fwd") || alias.includes("@")) return false;
  return MOVING_AVERAGES.has(alias.split("_")[0]!);
}

/**
 * A signed signal as a direction, or nothing on a bar where none fired.
 *
 * Zero is the *ordinary* state of one of these — a price-action series is `0`
 * on almost every bar — so it prints as nothing rather than as a mark, which is
 * the same rule a missing value follows. A row that shows a glyph on every bar
 * says nothing on the bar that matters.
 */
export function signalMark(y: number | null | undefined): string {
  if (y === null || y === undefined || !Number.isFinite(y) || y === 0) return "";
  return y > 0 ? "▲" : "▼";
}
